if (!customElements.get('company-invoice')) {
  class CompanyInvoice extends HTMLElement {
    constructor() {
      super();

      this.onToggleChange = this.onToggleChange.bind(this);
      this.onFieldInput = this.onFieldInput.bind(this);
      this.onFieldChange = this.onFieldChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onAdditionalCheckoutClick = this.onAdditionalCheckoutClick.bind(this);
    }

    connectedCallback() {
      this.toggle = this.querySelector('[data-company-invoice-toggle]');
      this.fields = this.querySelector('[data-company-invoice-fields]');
      this.requestedInput = this.querySelector('[data-company-invoice-requested]');
      this.inputs = Array.from(this.querySelectorAll('[data-company-invoice-field]'));
      this.status = this.querySelector('[data-company-invoice-status]');
      this.form = document.getElementById(this.dataset.formId);
      this.additionalCheckout = document.querySelector('[data-terms-additional]');

      if (!this.toggle || !this.fields || !this.requestedInput) return;

      this.toggle.addEventListener('change', this.onToggleChange);
      this.inputs.forEach((input) => {
        input.addEventListener('input', this.onFieldInput);
        input.addEventListener('change', this.onFieldChange);
      });
      this.form?.addEventListener('submit', this.onFormSubmit, true);
      this.additionalCheckout?.addEventListener('click', this.onAdditionalCheckoutClick, true);

      this.syncState(false);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('change', this.onToggleChange);
      this.inputs?.forEach((input) => {
        input.removeEventListener('input', this.onFieldInput);
        input.removeEventListener('change', this.onFieldChange);
      });
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.additionalCheckout?.removeEventListener('click', this.onAdditionalCheckoutClick, true);

      clearTimeout(this.saveTimer);
      this.abortController?.abort();
    }

    onToggleChange() {
      this.syncState(!this.toggle.checked);
      this.save();
    }

    onFieldInput(event) {
      event.target.setCustomValidity('');
      this.setStatus('');

      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.save(), 400);
    }

    onFieldChange(event) {
      event.target.setCustomValidity('');
      this.save();
    }

    onFormSubmit(event) {
      if (this.validate()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    }

    onAdditionalCheckoutClick(event) {
      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (this.dataset.saveState === 'saving' || this.dataset.saveState === 'error') {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setStatus(
          this.dataset.saveState === 'saving' ? this.dataset.savingText : this.dataset.saveError
        );
      }
    }

    syncState(clearValues) {
      const isRequested = this.toggle.checked;

      this.toggle.setAttribute('aria-expanded', String(isRequested));
      this.fields.hidden = !isRequested;
      this.requestedInput.value = isRequested ? 'Yes' : '';

      this.inputs.forEach((input) => {
        input.required = isRequested && input.hasAttribute('data-company-invoice-required');

        if (!isRequested) {
          input.setCustomValidity('');
          if (clearValues) input.value = '';
        }
      });
    }

    validate() {
      if (!this.toggle.checked) return true;

      const invalidInput = this.inputs.find((input) => input.required && input.value.trim() === '');
      if (!invalidInput) return true;

      invalidInput.setCustomValidity(invalidInput.dataset.requiredMessage || '');
      invalidInput.reportValidity();
      return false;
    }

    getAttributes() {
      const isRequested = this.toggle.checked;
      const attributes = {
        'Invoice required': isRequested ? 'Yes' : '',
      };

      this.inputs.forEach((input) => {
        attributes[input.dataset.attributeKey] = isRequested ? input.value.trim() : '';
      });

      return attributes;
    }

    get updateUrl() {
      if (window.FoxTheme?.routes?.cart_update_url) {
        return window.FoxTheme.routes.cart_update_url;
      }

      const root = window.Shopify?.routes?.root || '/';
      return `${root}cart/update.js`;
    }

    async save() {
      clearTimeout(this.saveTimer);
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;

      this.dataset.saveState = 'saving';
      this.setAttribute('aria-busy', 'true');
      this.setStatus('');

      try {
        const response = await fetch(this.updateUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: this.getAttributes() }),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('Unable to save company invoice attributes');

        this.dataset.saveState = 'saved';
        this.setStatus('');
      } catch (error) {
        if (error.name === 'AbortError') return;

        this.dataset.saveState = 'error';
        this.setStatus(this.dataset.saveError);
        console.error('Unable to save company invoice attributes:', error);
      } finally {
        if (this.abortController === controller) {
          this.removeAttribute('aria-busy');
          this.abortController = undefined;
        }
      }
    }

    setStatus(message) {
      if (!this.status) return;

      this.status.textContent = message || '';
      this.status.hidden = !message;
    }
  }

  customElements.define('company-invoice', CompanyInvoice);
}
